import { useMutation, useQueryClient } from "@tanstack/react-query";
import { dateValueToString } from "@/components/shared/formElements/DatePicker";
import { buildStudentTuitionApiFields } from "@/features/students/shared/studentTuitionPayload";
import { useAccessToken } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";
import { utcNoonFromYmd } from "@/lib/utils/date/date-only";
import { StudentsApi } from "@/openapi/generated";
import type { StudentEditFormSchemaType } from "@/schemas";

export const useStudentUpdate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
  options?: { onSuccess?: () => void },
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async ({
      studentId,
      formData,
    }: {
      studentId: number;
      formData: StudentEditFormSchemaType;
    }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(StudentsApi, accessToken);

      const dateOfBirthYmd = dateValueToString(formData.birthday);
      if (!dateOfBirthYmd) {
        throw new Error("Date of birth is required");
      }
      const dateOfBirth = utcNoonFromYmd(dateOfBirthYmd);

      const locationIds = (formData.locationIds ?? []).map(Number);
      if (locationIds.length === 0) {
        console.warn(
          "[useStudentUpdate] locationIds is empty – request may fail validation",
        );
      }

      const tuitionFields = buildStudentTuitionApiFields(
        formData.noCharge,
        formData.tuition,
      );

      const updatePayload = {
        studentId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        preferredName: formData.preferredName || null,
        dateOfBirth,
        gender: formData.gender as import("@/openapi/generated").Gender,
        isActive: formData.isActive ?? true,
        cellPhone: formData.cellPhone || null,
        homePhone: formData.homePhone || null,
        email: formData.email || null,
        address: formData.address || null,
        locationIds,
        medications: formData.medications || null,
        allergies: formData.allergies || null,
        specialNeeds: formData.specialNeeds || null,
        primaryDoctor: formData.primaryDoctor || null,
        postPublicly: formData.postPublicly ?? false,
        ...(typeof formData.recital === "boolean"
          ? { recital: formData.recital }
          : {}),
        discountPercentage: formData.discountPercentage ?? null,
        ...tuitionFields,
      };

      return service.apiStudentsPut({
        updateStudentRequest: updatePayload,
      });
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.UserStudentsGet],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Students, variables.studentId],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => {
          return (
            Array.isArray(query.queryKey) &&
            query.queryKey.some(
              (key) =>
                typeof key === "string" &&
                (key.includes("student") || key.includes("Student")),
            )
          );
        },
      });

      toast({
        title: "Student updated",
        description: "Student information has been successfully updated!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      options?.onSuccess?.();
    },
    onError: async (error: any) => {
      console.error("[useStudentUpdate] Update failed:", error);
      // Log response body so backend message is visible in console
      if (error?.response) {
        try {
          const body = await error.response
            .clone()
            .json()
            .catch(() => null);
          if (body) console.error("[useStudentUpdate] Server response:", body);
        } catch {
          // ignore
        }
      }
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to update student",
      );
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
